'use client';

import { formatNumber } from '@lichtblick/localization';
import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { TextCTA } from '@lichtblick/photon/atoms/text-cta/text-cta';
import { InfoXs } from '@lichtblick/photon/icons/symbols/info-xs';
import { Stepper } from '@lichtblick/photon/molecules/stepper';
import { Textfield } from '@lichtblick/photon/molecules/textfield';
import { Modal } from '@lichtblick/photon/organisms/modal/modal';
import { useEffect, useState } from 'react';

import { gasUsagePerSquareMeter, squareMeterLimit, usageLimitGas } from '../../constants';
import { ModalContent } from '../modals/modal-content';

const GAS_USAGE_MODAL_TITLE = 'So berechnet LichtBlick den Gasverbrauch';

const calculateUsage = (livingSpace: number) => String(livingSpace * gasUsagePerSquareMeter);

type GasUsageProps = {
  defaultLivingSpace?: string;
  defaultValue?: string;
  verboseLabel?: boolean;
};

export const GasUsage: React.FC<GasUsageProps> = ({ defaultLivingSpace, defaultValue, verboseLabel = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [livingSpace, setLivingSpace] = useState(defaultLivingSpace ? Number(defaultLivingSpace) : 30);
  const [usage, setUsage] = useState(calculateUsage(defaultValue ? Number(defaultValue) : livingSpace));

  useEffect(() => {
    setUsage(calculateUsage(livingSpace));
  }, [livingSpace]);

  return (
    <>
      <Stepper
        data-testid="livingSpace"
        errorMessage={`Die Wohnfläche muss zwischen ${squareMeterLimit.min} und ${squareMeterLimit.max} m² betragen`}
        label="Wohnfläche in m²"
        max={squareMeterLimit.max}
        min={squareMeterLimit.min}
        name="livingSpace"
        onChange={setLivingSpace}
        required
        value={livingSpace}
      />

      <Textfield
        data-testid="text-gasUsage"
        errorMessage={`Ein höherer Verbrauch als ${formatNumber(usageLimitGas.max)} kWh/Jahr ist nicht möglich.`}
        footer={
          <TextCTA
            ariaLabel="Information"
            as="button"
            icon={<InfoXs />}
            onClick={() => {
              setIsModalOpen(true);
            }}
            slim
            type="button"
            variant="thin"
          >
            So berechnet LichtBlick den Gasverbrauch
          </TextCTA>
        }
        inputMode="numeric"
        label={verboseLabel ? 'Dein Gasverbrauch' : 'Dein Verbrauch'}
        max={usageLimitGas.max}
        min={usageLimitGas.min}
        name="gasUsage"
        onChange={(e) => {
          setUsage(e.currentTarget.value);
        }}
        required
        rightSlot="kWh/Jahr"
        type="number"
        value={usage}
      />

      <Modal
        footerContent={
          <TextCTA as="button" data-testid="button-modal-close" onClick={() => setIsModalOpen(false)}>
            Ok, verstanden
          </TextCTA>
        }
        isOpen={isModalOpen}
        onPointerDownOutside={() => setIsModalOpen(false)}
        title={GAS_USAGE_MODAL_TITLE}
      >
        <ModalContent>
          <Headline renderAs={'h2'} size="s">
            {GAS_USAGE_MODAL_TITLE}
          </Headline>
          <Text renderAs={'p'}>
            Natürlich wisst nur ihr genau, was ihr verbraucht und könnt es deswegen auch direkt eintragen. Aus langer
            Erfahrung als Gasanbieter kann LichtBlick aber auch ziemlich gut schätzen: Im Schnitt werden pro
            Quadratmeter 150 kWh im Jahr verbraucht. Dabei ist natürlich miteingerechnet, dass im Sommer
            durchschnittlich deutlich weniger verbraucht wird als im Winter. Wenn zusätzlich mit Gas gekocht wird,
            kommen nochmal 1.000 kWh dazu. Diese müsstet ihr dann nur noch im Kalkulator hinzufügen, damit die Rechnung
            aufgeht. Doch weil auch wir bei der Berechnung nur mit Wasser kochen, könnt ihr den errechneten Wert
            jederzeit anpassen.
          </Text>
        </ModalContent>
      </Modal>
    </>
  );
};
